import React, { CSSProperties } from "react";
import styled from "styled-components";
import { LessonRecording } from "../common/api";
import { LessonImage } from "../common/LessonImage";
import { Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BORDER_WIDTH, LESSON_RECORDING_HEIGHT } from "../common/constants";
import {
  COLOR_BLACK,
  COLOR_GRAY_BORDER,
  COLOR_GRAY_99,
  COLOR_RED_BRIGHT,
  COLOR_TEXT_PRIMARY,
  COLOR_WHITE,
} from "../common/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis } from "@fortawesome/pro-solid-svg-icons";

const StyledLessonRecordingItemBody = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${({ borderWidth }) => `border: ${borderWidth}px solid ${COLOR_GRAY_BORDER};`}
  overflow: hidden;
  height: ${LESSON_RECORDING_HEIGHT}px;
  border-radius: 16px;
  background-color: ${COLOR_WHITE};
  & div {
    text-decoration: none;
  }
`;
const MetadataBody = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 20px;
`;
const Title = styled.div`
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ isReady }) => (isReady ? `${COLOR_TEXT_PRIMARY};` : `${COLOR_GRAY_99};`)}

  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 21px;

  margin-bottom: 2px;
`;
const Description = styled.div`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${({ isReady }) => (isReady ? `${COLOR_TEXT_PRIMARY};` : `${COLOR_GRAY_99};`)}
  margin-bottom: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const MetadataColumn = styled(Col)`
  color: ${({ isReady }) => (isReady ? COLOR_TEXT_PRIMARY : COLOR_GRAY_99)};
  font-size: 12px;
  line-height: 14px;
`;

const ImageBlock = styled.div`
  height: 100%;
  width: ${({ borderWidth }) => LESSON_RECORDING_HEIGHT - 2 * borderWidth}px;
  display: flex;
  justify-content: center;
  overflow: hidden;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

function formatDuration(seconds: number) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (hrs > 0) {
    parts.push(hrs.toString());
    parts.push(mins.toString().padStart(2, "0"));
  } else {
    parts.push(mins.toString());
  }
  parts.push(secs.toString().padStart(2, "0"));
  return parts.join(":");
}

export const LessonRecordingItemBody = ({
  recording,
  style,
  className,
  onMenuButtonClick,
}: {
  recording: LessonRecording;
  style?: CSSProperties;
  className?: string;
  onMenuButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { t } = useTranslation();
  const imageUrl = recording.cover_image_url;
  const isReady = recording.is_ready;

  return (
    <StyledLessonRecordingItemBody
      key={recording.id}
      style={style}
      className={className}
      borderWidth={BORDER_WIDTH}
    >
      <ImageBlock borderWidth={BORDER_WIDTH}>
        {imageUrl && (
          <LessonImage
            lazy
            thumbSize="large"
            url={imageUrl}
            alt={t("lesson_cover_content_description", {
              title: recording.lesson_title,
            })}
          />
        )}
      </ImageBlock>
      <MetadataBody>
        <Title isReady={isReady}>{recording.lesson_title}</Title>
        <Description isReady={isReady}>
          {new Date(recording.created_date).toLocaleString(navigator.language, {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}
        </Description>
        <div className="d-flex justify-content-between align-items-center">
          {isReady && (
            <MetadataColumn $isReady={true}>
              <div>{formatDuration(recording.length_seconds!)}</div>
            </MetadataColumn>
          )}
        </div>
      </MetadataBody>
      {isReady ? (
        <div className="align-items-center align-content-center mx-2">
          <IconButton
            onClick={(event) => {
              event.preventDefault();
              onMenuButtonClick(event);
            }}
          >
            <FontAwesomeIcon icon={faEllipsis} color={COLOR_BLACK} />
          </IconButton>
        </div>
      ) : (
        <div className="mt-auto mx-2">
          <p style={{ color: COLOR_RED_BRIGHT }}>{t("lesson_replays_processing")}</p>
        </div>
      )}
    </StyledLessonRecordingItemBody>
  );
};
